import { createContext, useContext, useState, useCallback, type ReactNode } from "react";
import type { VehicleData } from "@/types/vehicle";
import type { MaintenanceRecord } from "@/types/maintenance";
import type { BookingStatus } from "@/types/booking-status";
import {
  INITIAL_CUSTOMERS, INITIAL_VEHICLES, INITIAL_BOOKINGS, INITIAL_PAYMENTS,
  INITIAL_ALERTS, INITIAL_MAINTENANCE, INITIAL_INSPECTIONS,
  INITIAL_PICKUP_SESSIONS, INITIAL_RETURN_SESSIONS, INITIAL_QUOTES,
  INITIAL_CRM_INTEGRATIONS, DEMO_BUSINESS, INITIAL_BRANCHES,
  type DemoCustomer, type DemoBooking, type DemoPayment, type DemoAlert,
  type DemoInspection, type DemoPickupSession, type DemoReturnSession,
  type DemoQuote, type DemoCrmIntegration, type DemoBranch,
} from "@/data/demoMockData";

function uid() { return crypto.randomUUID(); }
function now() { return new Date().toISOString(); }

export interface DemoStore {
  // Data
  customers: DemoCustomer[];
  vehicles: VehicleData[];
  bookings: DemoBooking[];
  payments: DemoPayment[];
  alerts: DemoAlert[];
  maintenance: MaintenanceRecord[];
  inspections: DemoInspection[];
  pickupSessions: DemoPickupSession[];
  returnSessions: DemoReturnSession[];
  quotes: DemoQuote[];
  crmIntegrations: DemoCrmIntegration[];
  branches: DemoBranch[];
  business: typeof DEMO_BUSINESS;

  // Customers
  upsertCustomer: (c: Partial<DemoCustomer> & { id?: string }) => DemoCustomer;
  deleteCustomer: (id: string) => void;

  // Vehicles
  upsertVehicle: (v: Partial<VehicleData> & { id?: string }) => VehicleData;
  deleteVehicle: (id: string) => void;

  // Bookings
  upsertBooking: (b: any) => DemoBooking;
  deleteBooking: (id: string) => void;
  updateBookingStatus: (id: string, status: BookingStatus) => void;

  // Payments
  getPaymentsForBooking: (bookingId: string) => DemoPayment[];
  createPayment: (p: Omit<DemoPayment, "id" | "created_at"> & { booking_id: string }) => DemoPayment;

  // Alerts
  markAlertRead: (id: string) => void;
  markAllAlertsRead: () => void;
  resolveAlert: (id: string) => void;

  // Maintenance
  upsertMaintenance: (r: any) => MaintenanceRecord;
  deleteMaintenance: (id: string) => void;
  toggleMaintenanceComplete: (id: string, completed: boolean) => void;

  // Inspections
  getInspectionsForBooking: (bookingId: string) => DemoInspection[];
  createInspection: (i: Omit<DemoInspection, "id" | "created_at">) => DemoInspection;

  // Pickup sessions
  getPickupSession: (bookingId: string) => DemoPickupSession | null;
  createPickupSession: (bookingId: string) => DemoPickupSession;
  updatePickupSession: (id: string, updates: Record<string, any>) => void;
  completePickupSession: (id: string) => void;

  // Return sessions
  getReturnSession: (bookingId: string) => DemoReturnSession | null;
  createReturnSession: (bookingId: string) => DemoReturnSession;
  updateReturnSession: (id: string, updates: Record<string, any>) => void;
  completeReturnSession: (id: string) => void;

  // Quotes
  createQuote: (q: Omit<DemoQuote, "id" | "created_at">) => DemoQuote;

  // CRM
  upsertCrmIntegration: (i: Partial<DemoCrmIntegration>) => DemoCrmIntegration;
  deleteCrmIntegration: (id: string) => void;
  toggleCrmIntegrationStatus: (id: string) => void;

  // Branches
  upsertBranch: (b: Partial<DemoBranch> & { id?: string }) => DemoBranch;
  deleteBranch: (id: string) => void;

  // Business
  updateBusiness: (updates: Record<string, unknown>) => void;

  // Reset
  reset: () => void;
}

const DemoStoreContext = createContext<DemoStore | null>(null);

function clone<T>(data: T): T { return JSON.parse(JSON.stringify(data)); }

function dateAtOffset(days: number, hour = 12) {
  const date = new Date();
  date.setDate(date.getDate() + days);
  date.setHours(hour, 0, 0, 0);
  return date.toISOString();
}

function dateOnlyAtOffset(days: number) {
  return dateAtOffset(days).slice(0, 10);
}

function shortDate(days: number) {
  return new Date(dateAtOffset(days)).toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

function buildDemoSnapshot() {
  const bookings = clone(INITIAL_BOOKINGS);
  const payments = clone(INITIAL_PAYMENTS);
  const alerts = clone(INITIAL_ALERTS);
  const maintenance = clone(INITIAL_MAINTENANCE);
  const inspections = clone(INITIAL_INSPECTIONS);
  const returnSessions = clone(INITIAL_RETURN_SESSIONS);

  const bookingDates: Record<string, { start: number; end: number; actualReturn?: number }> = {
    b1: { start: -5, end: -1 },
    b2: { start: 0, end: 4 },
    b3: { start: -7, end: 0 },
    b4: { start: -3, end: 0 },
    b5: { start: 2, end: 5 },
    b6: { start: 0, end: 3 },
    b7: { start: -12, end: -8, actualReturn: -8 },
    b8: { start: -10, end: -9 },
  };

  bookings.forEach((booking) => {
    const offsets = bookingDates[booking.id];
    if (!offsets) return;
    booking.start_date = dateAtOffset(offsets.start, 9);
    booking.end_date = dateAtOffset(offsets.end, offsets.end === 0 ? 23 : 18);
    booking.actual_return_date = offsets.actualReturn != null ? dateOnlyAtOffset(offsets.actualReturn) : null;
  });

  const paymentOffsets: Record<string, number> = {
    p1: -5,
    p2: -5,
    p3: 0,
    p4: 0,
    p5: -3,
    p6: -3,
    p7: -12,
    p8: -8,
    p9: -7,
    p10: 0,
    p11: 0,
  };

  payments.forEach((payment) => {
    const offset = paymentOffsets[payment.id];
    if (offset == null) return;
    payment.created_at = dateAtOffset(offset, 10);
    payment.paid_at = payment.status === "held" ? null : dateAtOffset(offset, 10);
  });

  const alertOffsets: Record<string, number> = {
    a1: -1,
    a2: -1,
    a3: -2,
    a4: 0,
    a5: 0,
    a6: -3,
  };

  alerts.forEach((alert) => {
    const offset = alertOffsets[alert.id];
    if (offset == null) return;
    alert.created_at = dateAtOffset(offset, 8);
  });

  const alertMessages: Record<string, string> = {
    a1: `Honda Civic (B 567890) insurance expires on ${shortDate(12)}`,
    a2: "Hyundai Tucson (B 345678) return is in progress today",
    a3: "Toyota Corolla (B 123456) oil change overdue by 2,450 km",
    a4: "BMW X3 is confirmed and waiting for Karim Jaber to collect",
    a5: "Lina Farah - BMW X3 is scheduled to return today",
    a6: `Body repair on Toyota Corolla was due ${shortDate(-3)} - escalated`,
  };

  alerts.forEach((alert) => {
    alert.message = alertMessages[alert.id] ?? alert.message;
  });

  const maintenanceDates: Record<string, { created: number; due?: number; completed?: number }> = {
    m1: { created: -35, completed: -35 },
    m2: { created: -2 },
    m3: { created: 0, due: 3 },
    m4: { created: -1, due: 2 },
    m5: { created: -45, completed: -45 },
    m6: { created: -2, due: 5 },
  };

  maintenance.forEach((record) => {
    const offsets = maintenanceDates[record.id];
    if (!offsets) return;
    record.created_at = dateAtOffset(offsets.created, 9);
    record.due_date = offsets.due != null ? dateAtOffset(offsets.due, 18) : record.due_date;
    record.completed_at = offsets.completed != null ? dateAtOffset(offsets.completed, 16) : record.completed_at;
  });

  inspections.forEach((inspection, index) => {
    inspection.created_at = dateAtOffset(index < 2 ? -7 : -12, 9);
  });

  returnSessions.forEach((session) => {
    session.created_at = dateAtOffset(0, 14);
  });

  return {
    customers: clone(INITIAL_CUSTOMERS),
    vehicles: clone(INITIAL_VEHICLES),
    bookings,
    payments,
    alerts,
    maintenance,
    inspections,
    pickupSessions: clone(INITIAL_PICKUP_SESSIONS),
    returnSessions,
    quotes: clone(INITIAL_QUOTES),
    crmIntegrations: clone(INITIAL_CRM_INTEGRATIONS),
    branches: clone(INITIAL_BRANCHES),
    business: clone(DEMO_BUSINESS),
  };
}

export function DemoStoreProvider({ children }: { children: ReactNode }) {
  const [initialDemoSnapshot] = useState(buildDemoSnapshot);
  const [customers, setCustomers] = useState(() => initialDemoSnapshot.customers);
  const [vehicles, setVehicles] = useState(() => initialDemoSnapshot.vehicles);
  const [bookings, setBookings] = useState(() => initialDemoSnapshot.bookings);
  const [payments, setPayments] = useState(() => initialDemoSnapshot.payments);
  const [alerts, setAlerts] = useState(() => initialDemoSnapshot.alerts);
  const [maintenance, setMaintenance] = useState(() => initialDemoSnapshot.maintenance);
  const [inspections, setInspections] = useState(() => initialDemoSnapshot.inspections);
  const [pickupSessions, setPickupSessions] = useState(() => initialDemoSnapshot.pickupSessions);
  const [returnSessions, setReturnSessions] = useState(() => initialDemoSnapshot.returnSessions);
  const [quotes, setQuotes] = useState(() => initialDemoSnapshot.quotes);
  const [crmIntegrations, setCrmIntegrations] = useState(() => initialDemoSnapshot.crmIntegrations);
  const [branches, setBranches] = useState(() => initialDemoSnapshot.branches);
  const [business, setBusiness] = useState(() => initialDemoSnapshot.business);

  // ── Customers ──
  const upsertCustomer = useCallback((c: Partial<DemoCustomer> & { id?: string }) => {
    if (c.id && customers.some((x) => x.id === c.id)) {
      let updated: DemoCustomer = {} as any;
      setCustomers((prev) => prev.map((x) => {
        if (x.id === c.id) { updated = { ...x, ...c } as DemoCustomer; return updated; }
        return x;
      }));
      return updated;
    }
    const newId = c.id || uid();
    const newC: DemoCustomer = { full_name: c.full_name || "", phone: c.phone || null, email: c.email || null, flags: c.flags || [], notes: c.notes || null, created_at: now(), ...c, id: newId };
    setCustomers((prev) => [newC, ...prev]);
    return newC;
  }, [customers]);

  const deleteCustomer = useCallback((id: string) => setCustomers((p) => p.filter((x) => x.id !== id)), []);

  // ── Vehicles ──
  const upsertVehicle = useCallback((v: Partial<VehicleData> & { id?: string }) => {
    if (v.id && vehicles.some((x) => x.id === v.id)) {
      let updated: VehicleData = {} as any;
      setVehicles((prev) => prev.map((x) => {
        if (x.id === v.id) { updated = { ...x, ...v } as VehicleData; return updated; }
        return x;
      }));
      return updated;
    }
    const newV: VehicleData = { ...INITIAL_VEHICLES[0], ...v, id: v.id || uid() } as VehicleData;
    setVehicles((prev) => [newV, ...prev]);
    return newV;
  }, [vehicles]);

  const deleteVehicle = useCallback((id: string) => setVehicles((p) => p.filter((x) => x.id !== id)), []);

  // ── Bookings ──
  const upsertBooking = useCallback((b: any) => {
    const cust = customers.find((x) => x.id === b.customer_id);
    const veh = vehicles.find((x) => x.id === b.vehicle_id);
    if (b.id && bookings.some((x) => x.id === b.id)) {
      let updated: DemoBooking = {} as any;
      setBookings((prev) => prev.map((x) => {
        if (x.id === b.id) {
          updated = { ...x, ...b, customer: cust ? { id: cust.id, full_name: cust.full_name, phone: cust.phone, email: cust.email } : x.customer, vehicle: veh ? { id: veh.id, make: veh.make, model: veh.model, plate_number: veh.plate_number, daily_rate: veh.daily_rate, status: veh.status } : x.vehicle };
          return updated;
        }
        return x;
      }));
      return updated;
    }
    const newB: DemoBooking = {
      id: uid(), status: b.status || "inquiry", start_date: b.start_date || "", end_date: b.end_date || "",
      daily_rate: b.daily_rate ?? null, total_price: b.total_price ?? null, notes: b.notes ?? null,
      pickup_mileage: null, return_mileage: null, actual_return_date: null, signature_url: null,
      customer_id: b.customer_id || null, vehicle_id: b.vehicle_id || null,
      customer: cust ? { id: cust.id, full_name: cust.full_name, phone: cust.phone, email: cust.email } : null,
      vehicle: veh ? { id: veh.id, make: veh.make, model: veh.model, plate_number: veh.plate_number, daily_rate: veh.daily_rate, status: veh.status } : null,
    };
    setBookings((prev) => [newB, ...prev]);
    return newB;
  }, [customers, vehicles, bookings]);

  const deleteBooking = useCallback((id: string) => setBookings((p) => p.filter((x) => x.id !== id)), []);

  const updateBookingStatus = useCallback((id: string, status: BookingStatus) => {
    setBookings((prev) => prev.map((x) => x.id === id ? { ...x, status } : x));
  }, []);

  // ── Payments ──
  const getPaymentsForBooking = useCallback((bookingId: string) => payments.filter((p) => p.booking_id === bookingId), [payments]);

  const createPayment = useCallback((p: Omit<DemoPayment, "id" | "created_at"> & { booking_id: string }) => {
    const newP: DemoPayment = { ...p, id: uid(), created_at: now() };
    setPayments((prev) => [...prev, newP]);
    return newP;
  }, []);

  // ── Alerts ──
  const markAlertRead = useCallback((id: string) => setAlerts((p) => p.map((a) => a.id === id ? { ...a, is_read: true } : a)), []);
  const markAllAlertsRead = useCallback(() => setAlerts((p) => p.map((a) => ({ ...a, is_read: true }))), []);
  const resolveAlert = useCallback((id: string) => setAlerts((p) => p.map((a) => a.id === id ? { ...a, resolved: true } : a)), []);

  // ── Maintenance ──
  const upsertMaintenance = useCallback((r: any) => {
    const veh = vehicles.find((v) => v.id === r.vehicle_id);
    if (r.id && maintenance.some((x) => x.id === r.id)) {
      let updated: MaintenanceRecord = {} as any;
      setMaintenance((prev) => prev.map((x) => {
        if (x.id === r.id) { updated = { ...x, ...r, vehicle: veh ? { id: veh.id, make: veh.make, model: veh.model, plate_number: veh.plate_number, mileage: veh.mileage } : x.vehicle } as MaintenanceRecord; return updated; }
        return x;
      }));
      return updated;
    }
    const newR: MaintenanceRecord = { ...r, id: r.id || uid(), created_at: r.created_at || now(), vehicle: veh ? { id: veh.id, make: veh.make, model: veh.model, plate_number: veh.plate_number, mileage: veh.mileage } : undefined };
    setMaintenance((prev) => [newR, ...prev]);
    return newR;
  }, [vehicles, maintenance]);

  const deleteMaintenance = useCallback((id: string) => setMaintenance((p) => p.filter((x) => x.id !== id)), []);
  const toggleMaintenanceComplete = useCallback((id: string, completed: boolean) => {
    setMaintenance((p) => p.map((x) => x.id === id ? { ...x, completed, status: completed ? null : "pending" } : x));
  }, []);

  // ── Inspections ──
  const getInspectionsForBooking = useCallback((bookingId: string) => inspections.filter((i) => i.booking_id === bookingId), [inspections]);

  const createInspection = useCallback((i: Omit<DemoInspection, "id" | "created_at">) => {
    const newI: DemoInspection = { ...i, id: uid(), created_at: now() };
    setInspections((prev) => [...prev, newI]);
    return newI;
  }, []);

  // ── Pickup Sessions ──
  const getPickupSession = useCallback((bookingId: string) => pickupSessions.find((s) => s.booking_id === bookingId) || null, [pickupSessions]);

  const createPickupSession = useCallback((bookingId: string) => {
    const s: DemoPickupSession = { id: uid(), booking_id: bookingId, vehicle_id: null, status: "in_progress", id_verified: false, license_verified: false, pre_inspection_done: false, car_cleaned: false, fuel_noted: false, insurance_verified: false, keys_available: false, deposit_terms_reviewed: false, customer_acknowledged: false, acknowledgment_method: null, completed_at: null, created_at: now() };
    setPickupSessions((prev) => [...prev, s]);
    updateBookingStatus(bookingId, "pickup_in_progress");
    return s;
  }, [updateBookingStatus]);

  const updatePickupSession = useCallback((id: string, updates: Record<string, any>) => {
    setPickupSessions((p) => p.map((s) => s.id === id ? { ...s, ...updates } : s));
  }, []);

  const completePickupSession = useCallback((id: string) => {
    setPickupSessions((p) => p.map((s) => s.id === id ? { ...s, status: "completed", completed_at: now() } : s));
  }, []);

  // ── Return Sessions ──
  const getReturnSession = useCallback((bookingId: string) => returnSessions.find((s) => s.booking_id === bookingId) || null, [returnSessions]);

  const createReturnSession = useCallback((bookingId: string) => {
    const s: DemoReturnSession = { id: uid(), booking_id: bookingId, vehicle_id: null, status: "in_progress", return_mileage: null, return_fuel_level: null, damage_found: null, damage_notes: null, extra_km_charge: null, fuel_penalty: null, late_fee: null, damage_charge: null, cleaning_fee: null, deposit_resolution: null, deposit_withheld_amount: null, deposit_withhold_reason: null, completed_at: null, created_at: now() };
    setReturnSessions((prev) => [...prev, s]);
    updateBookingStatus(bookingId, "return_in_progress");
    return s;
  }, [updateBookingStatus]);

  const updateReturnSession = useCallback((id: string, updates: Record<string, any>) => {
    setReturnSessions((p) => p.map((s) => s.id === id ? { ...s, ...updates } : s));
  }, []);

  const completeReturnSession = useCallback((id: string) => {
    setReturnSessions((p) => p.map((s) => s.id === id ? { ...s, status: "completed", completed_at: now() } : s));
  }, []);

  // ── Quotes ──
  const createQuote = useCallback((q: Omit<DemoQuote, "id" | "created_at">) => {
    const newQ: DemoQuote = { ...q, id: uid(), created_at: now() };
    setQuotes((prev) => [...prev, newQ]);
    return newQ;
  }, []);

  // ── CRM ──
  const upsertCrmIntegration = useCallback((i: Partial<DemoCrmIntegration>) => {
    if (i.id && crmIntegrations.some((x) => x.id === i.id)) {
      let updated: DemoCrmIntegration = {} as any;
      setCrmIntegrations((p) => p.map((x) => { if (x.id === i.id) { updated = { ...x, ...i } as DemoCrmIntegration; return updated; } return x; }));
      return updated;
    }
    const newI: DemoCrmIntegration = { id: uid(), provider: i.provider || "Custom", webhook_url: i.webhook_url || "", api_key: i.api_key || uid(), status: "active", config: i.config || {}, ...i };
    setCrmIntegrations((p) => [...p, newI]);
    return newI;
  }, [crmIntegrations]);

  const deleteCrmIntegration = useCallback((id: string) => setCrmIntegrations((p) => p.filter((x) => x.id !== id)), []);

  const toggleCrmIntegrationStatus = useCallback((id: string) => {
    setCrmIntegrations((p) => p.map((x) => x.id === id ? { ...x, status: x.status === "active" ? "inactive" : "active" } : x));
  }, []);

  // ── Branches ──
  const upsertBranch = useCallback((b: Partial<DemoBranch> & { id?: string }) => {
    if (b.id && branches.some((x) => x.id === b.id)) {
      let updated: DemoBranch = {} as any;
      setBranches((prev) => prev.map((x) => {
        if (x.id !== b.id) return x;
        updated = { ...x, ...b, updated_at: now() };
        return updated;
      }));
      return updated;
    } else {
      const created: DemoBranch = { business_id: "demo-business", name: "", address: null, city: null, country: null, phone: null, timezone: "UTC", is_active: true, created_at: now(), updated_at: now(), ...b, id: b.id || uid() };
      setBranches((prev) => [...prev, created]);
      return created;
    }
  }, [branches]);

  const deleteBranch = useCallback((id: string) => setBranches((p) => p.filter((x) => x.id !== id)), []);

  // ── Business ──
  const updateBusiness = useCallback((updates: Record<string, unknown>) => {
    setBusiness((prev) => ({ ...prev, ...updates }));
  }, []);

  // ── Reset ──
  const reset = useCallback(() => {
    const snapshot = buildDemoSnapshot();
    setCustomers(snapshot.customers);
    setVehicles(snapshot.vehicles);
    setBookings(snapshot.bookings);
    setPayments(snapshot.payments);
    setAlerts(snapshot.alerts);
    setMaintenance(snapshot.maintenance);
    setInspections(snapshot.inspections);
    setPickupSessions(snapshot.pickupSessions);
    setReturnSessions(snapshot.returnSessions);
    setQuotes(snapshot.quotes);
    setCrmIntegrations(snapshot.crmIntegrations);
    setBranches(snapshot.branches);
    setBusiness(snapshot.business);
  }, []);

  const store: DemoStore = {
    customers, vehicles, bookings, payments, alerts, maintenance, inspections,
    pickupSessions, returnSessions, quotes, crmIntegrations, branches, business,
    upsertCustomer, deleteCustomer,
    upsertVehicle, deleteVehicle,
    upsertBooking, deleteBooking, updateBookingStatus,
    getPaymentsForBooking, createPayment,
    markAlertRead, markAllAlertsRead, resolveAlert,
    upsertMaintenance, deleteMaintenance, toggleMaintenanceComplete,
    getInspectionsForBooking, createInspection,
    getPickupSession, createPickupSession, updatePickupSession, completePickupSession,
    getReturnSession, createReturnSession, updateReturnSession, completeReturnSession,
    createQuote,
    upsertCrmIntegration, deleteCrmIntegration, toggleCrmIntegrationStatus,
    upsertBranch, deleteBranch,
    updateBusiness,
    reset,
  };

  return <DemoStoreContext.Provider value={store}>{children}</DemoStoreContext.Provider>;
}

export function useDemoStore(): DemoStore {
  const ctx = useContext(DemoStoreContext);
  if (!ctx) throw new Error("useDemoStore must be used within DemoStoreProvider");
  return ctx;
}

export function useDemoStoreOptional(): DemoStore | null {
  return useContext(DemoStoreContext);
}
